import { RefObject, useCallback, useEffect, useState } from 'react';
import { computeDropdownMaxHeight, DROPDOWN_MOBILE_MAX_WIDTH_PX } from './addSearchArgs';

/**
 * Asana 1219947685097226: the results dropdown's max-height, measured from its
 * top edge to the bottom of the viewport. Recomputed on open, on window resize,
 * on page scroll while open and, on phones, on visualViewport changes so the
 * on-screen keyboard is accounted for. Returns null while closed.
 */
export const useDropdownMaxHeight = (overlayRef: RefObject<HTMLElement | null>, isOpen: boolean): number | null => {
  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  const measure = useCallback(() => {
    const el = overlayRef.current;
    if (!el) return;
    const isPhone = window.innerWidth < DROPDOWN_MOBILE_MAX_WIDTH_PX;
    const viewportHeight = isPhone && window.visualViewport ? window.visualViewport.height : window.innerHeight;
    setMaxHeight(computeDropdownMaxHeight(viewportHeight, el.getBoundingClientRect().top));
  }, [overlayRef]);

  useEffect(() => {
    if (!isOpen) {
      setMaxHeight(null);
      return;
    }
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { capture: true, passive: true });
    const vv = window.visualViewport;
    vv?.addEventListener('resize', measure);
    vv?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, { capture: true } as EventListenerOptions);
      vv?.removeEventListener('resize', measure);
      vv?.removeEventListener('scroll', measure);
    };
  }, [isOpen, measure]);

  return maxHeight;
};
